import React, { useState } from 'react';
import BentoCard from '../ui/BentoCard';
import MaterialIcon from '../ui/MaterialIcon';
import { useLoyalty } from '../../contexts/LoyaltyContext';
import { LOYALTY_PROGRAMS_BY_ID, getNextTier, estimateValue } from '../../data/loyaltyPrograms';

const ACCENT_BG_PAIRS = [
  { bg: 'from-blue-500/5 to-blue-500/10', bar: 'bg-blue-500', track: 'bg-blue-100' },
  { bg: 'from-purple-500/5 to-purple-500/10', bar: 'bg-purple-500', track: 'bg-purple-100' },
  { bg: 'from-amber-500/5 to-amber-500/10', bar: 'bg-amber-500', track: 'bg-amber-100' },
  { bg: 'from-rose-500/5 to-rose-500/10', bar: 'bg-rose-500', track: 'bg-rose-100' },
  { bg: 'from-teal-500/5 to-teal-500/10', bar: 'bg-teal-500', track: 'bg-teal-100' },
  { bg: 'from-indigo-500/5 to-indigo-500/10', bar: 'bg-indigo-500', track: 'bg-indigo-100' },
];

function ProgramCard({ enrolled, index, onRemove }) {
  const [showMenu, setShowMenu] = useState(false);
  const program = LOYALTY_PROGRAMS_BY_ID.get(enrolled.programId);
  if (!program) return null;

  const pair = ACCENT_BG_PAIRS[index % ACCENT_BG_PAIRS.length];
  const nextTier = getNextTier(enrolled.programId, enrolled.qualifyingUnits ?? enrolled.balance);
  const estValue = estimateValue(enrolled.programId, enrolled.balance);

  return (
    <BentoCard className={`bg-gradient-to-br ${pair.bg} relative`}>
      {/* Menu button */}
      <button
        onClick={() => setShowMenu((v) => !v)}
        className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors"
      >
        <MaterialIcon name="more_vert" size={18} className="text-on-surface-variant" />
      </button>
      {showMenu && (
        <div className="absolute top-10 right-3 bg-white rounded-xl shadow-card-hover border border-outline-variant/10 z-10 overflow-hidden">
          <button
            onClick={() => { onRemove(enrolled.programId); setShowMenu(false); }}
            className="flex items-center gap-2 px-4 py-3 text-sm text-error hover:bg-error/5 w-full text-left"
          >
            <MaterialIcon name="delete" size={16} className="text-error" />
            Remove
          </button>
        </div>
      )}

      <div className="flex items-start gap-3 pr-8">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${program.accentColor}18` }}
        >
          <MaterialIcon name={program.icon} size={22} style={{ color: program.accentColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-on-surface truncate">{program.name}</p>
          <p className="text-xs text-on-surface-variant mt-0.5">
            {enrolled.balance.toLocaleString()} {program.unit}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3">
        <span
          className="text-xs font-semibold px-2.5 py-1 rounded-full text-white"
          style={{ backgroundColor: program.accentColor }}
        >
          {enrolled.tier}
        </span>
        <span className="text-xs text-on-surface-variant">
          ≈ ${estValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
        </span>
      </div>

      {/* Progress to next tier */}
      {nextTier ? (
        <div className="mt-3">
          <div className={`w-full h-1 rounded-full ${pair.track}`}>
            <div
              className={`h-full rounded-full ${pair.bar} transition-all`}
              style={{ width: `${nextTier.progress * 100}%` }}
            />
          </div>
          <p className="text-xs text-on-surface-variant mt-1.5">
            {nextTier.remaining.toLocaleString()} {program.category === 'hotel' ? 'nights' : program.unit} to {nextTier.name}
          </p>
        </div>
      ) : (
        <div className="mt-3">
          <p className="text-xs text-success font-medium">✦ Max tier achieved</p>
        </div>
      )}
    </BentoCard>
  );
}

function LoyaltyProgramsGrid({ onAddProgram }) {
  const { programs, removeProgram } = useLoyalty();

  if (programs.length === 0) {
    return (
      <div>
        <h2 className="text-base font-semibold text-on-surface mb-3">Active Programs</h2>
        <BentoCard className="flex flex-col items-center py-10 gap-3">
          <MaterialIcon name="loyalty" size={44} className="text-on-surface-variant/30" />
          <p className="text-sm font-semibold text-on-surface">No programs yet</p>
          <p className="text-xs text-on-surface-variant text-center max-w-[200px]">
            Add your loyalty programs to track balances and tier status.
          </p>
          <button
            onClick={onAddProgram}
            className="mt-2 px-5 py-2.5 bg-primary text-on-primary text-sm font-semibold rounded-full"
          >
            Add Your First Program
          </button>
        </BentoCard>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-base font-semibold text-on-surface mb-3">Active Programs</h2>
      <div className="grid grid-cols-1 gap-3">
        {programs.map((p, i) => (
          <ProgramCard key={p.programId} enrolled={p} index={i} onRemove={removeProgram} />
        ))}
      </div>
    </div>
  );
}

export default LoyaltyProgramsGrid;
